import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import "@/assets/css/phoneinput-dark.css";
import "@/assets/css/phoneinput-dark-global.css";
import { registerCourierApplication } from "@/services/apiService";

export default function CourierRegistrationForm() {
  // Inline dark mode override for react-phone-input-2
  React.useEffect(() => {
    const styleId = "phoneinput-dark-override";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
          html.dark .react-tel-input .form-control,
          body.dark .react-tel-input .form-control {
            background-color: #232323 !important;
            color: #f3f4f6 !important;
            border-color: #444 !important;
          }
          html.dark .react-tel-input .flag-dropdown,
          body.dark .react-tel-input .flag-dropdown {
            background-color: #232323 !important;
            border-color: #444 !important;
          }
          html.dark .react-tel-input .country-list,
          body.dark .react-tel-input .country-list {
            background-color: #232323 !important;
            color: #f3f4f6 !important;
            border-color: #444 !important;
          }
          html.dark .react-tel-input .country-list .country,
          body.dark .react-tel-input .country-list .country {
            color: #f3f4f6 !important;
          }
          html.dark .react-tel-input .selected-flag,
          body.dark .react-tel-input .selected-flag {
            background-color: #232323 !important;
            border-color: #444 !important;
          }
        `;
      document.head.appendChild(style);
    }
  }, []);
  const [formData, setFormData] = useState({
    address: "",
    city: "",
    company: "",
    country: "",
    email: "",
    idDocument: undefined as File | undefined,
    idNumber: "",
    licenseDocument: undefined as File | undefined,
    name: "",
    password: "",
    phone: "",
    proofOfAddress: undefined as File | undefined,
    role: "CourierOwner",
    surname: "",
    terms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<"error" | "success">("success");
  const [errorDetail, setErrorDetail] = useState<null | string>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: typeof formData) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value: string) => {
    setFormData((prev) => ({ ...prev, phone: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files, name } = e.target;
    if (files && files[0]) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const result = await registerCourierApplication(formData);
      if (result.success) {
        setModalType("success");
        setErrorDetail(null);
        setShowModal(true);
      } else {
        // User-friendly error extraction
        let friendly =
          "There was an error submitting your registration. Please check your information and try again.";
        // Duplicate user/email/phone
        if (
          result.message &&
          typeof result.message === "string" &&
          result.message.toLowerCase().includes("already exist")
        ) {
          friendly =
            "An account with this email or phone number already exists. Please use a different email or log in.";
        } else if (result.errors && typeof result.errors === "object") {
          // Show first validation error
          const firstError = Object.values(result.errors)[0];
          if (Array.isArray(firstError)) {
            friendly = firstError[0];
          } else if (typeof firstError === "string") {
            friendly = firstError;
          }
        }
        setModalType("error");
        setErrorDetail(friendly);
        setShowModal(true);
      }
    } catch (error: any) {
      let friendly = "Network error. Please try again.";
      // Axios error with response
      if (error && error.response) {
        const status = error.response.status;
        const data = error.response.data;
        if (status === 422) {
          if (
            typeof data === "string" &&
            data.toLowerCase().includes("already exist")
          ) {
            friendly =
              "An account with this email or phone number already exists. Please use a different email or log in.";
          } else if (
            data &&
            typeof data === "object" &&
            data.message &&
            data.message.toLowerCase().includes("already exist")
          ) {
            friendly =
              "An account with this email or phone number already exists. Please use a different email or log in.";
          } else if (data && typeof data === "object" && data.errors) {
            const firstError = Object.values(data.errors)[0];
            if (Array.isArray(firstError)) {
              friendly = firstError[0];
            } else if (typeof firstError === "string") {
              friendly = firstError;
            }
          } else {
            friendly =
              "Some of your information is invalid. Please review and try again.";
          }
        }
      } else if (
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof (error as { message?: unknown }).message === "string"
      ) {
        friendly = (error as { message: string }).message;
      }
      setModalType("error");
      setErrorDetail(friendly);
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      className="mx-auto mt-4 w-full max-w-2xl rounded-2xl border border-base-200 bg-base-100 p-4 shadow-2xl dark:bg-base-200 sm:mt-8 sm:rounded-3xl sm:p-6 lg:p-10"
      onSubmit={handleSubmit}
    >
      <h2 className="mb-6 text-center text-2xl font-extrabold text-primary drop-shadow-lg sm:mb-8 sm:text-3xl lg:mb-10 lg:text-4xl">
        Become a Courier
      </h2>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:mb-8 sm:gap-6 md:grid-cols-2 md:gap-8">
        <div>
          <label className="mb-1 block text-sm font-semibold sm:mb-2 sm:text-base">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            autoComplete="given-name"
            className="input input-sm input-bordered w-full sm:input-md"
            name="name"
            onChange={handleInputChange}
            placeholder="Your first name"
            required
            value={formData.name}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold sm:mb-2 sm:text-base">
            Surname <span className="text-red-500">*</span>
          </label>
          <input
            autoComplete="family-name"
            className="input input-sm input-bordered w-full sm:input-md"
            name="surname"
            onChange={handleInputChange}
            placeholder="Your surname"
            required
            value={formData.surname}
          />
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-semibold sm:mb-2 sm:text-base">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            autoComplete="email"
            className="input input-sm input-bordered w-full sm:input-md"
            name="email"
            onChange={handleInputChange}
            placeholder="you@email.com"
            required
            type="email"
            value={formData.email}
          />
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-semibold sm:mb-2 sm:text-base">
            Password <span className="text-red-500">*</span>
          </label>
          <input
            autoComplete="new-password"
            className="input input-sm input-bordered w-full sm:input-md"
            name="password"
            onChange={handleInputChange}
            placeholder="Create a password"
            required
            type="password"
            value={formData.password}
          />
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-semibold sm:mb-2 sm:text-base">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <PhoneInput
            buttonClass="phoneinput-dark-btn dark:!bg-[#232323] dark:!text-[#f3f4f6]"
            containerClass="w-full"
            country={"za"}
            data-theme={
              document.documentElement.classList.contains("dark")
                ? "dark"
                : "light"
            }
            dropdownClass="phoneinput-dark-dropdown dark:!bg-[#232323] dark:!text-[#f3f4f6]"
            inputClass="input input-bordered input-sm sm:input-md w-full phoneinput-dark dark:!bg-[#232323] dark:!text-[#f3f4f6] dark:!border-[#444]"
            onChange={handlePhoneChange}
            value={formData.phone}
          />
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-semibold sm:mb-2 sm:text-base">
            Company Name <span className="text-red-500">*</span>
          </label>
          <input
            className="input input-sm input-bordered w-full sm:input-md"
            name="company"
            onChange={handleInputChange}
            placeholder="Company name"
            required
            value={formData.company}
          />
        </div>
        <div className="md:col-span-2">
          <label className="flex items-start gap-2 sm:items-center">
            <input
              checked={formData.terms}
              className="checkbox checkbox-sm mt-0.5 sm:checkbox-md sm:mt-0"
              name="terms"
              onChange={handleCheckboxChange}
              required
              type="checkbox"
            />
            <span className="text-sm font-semibold sm:text-base">
              I agree to the terms and conditions{" "}
              <span className="text-red-500">*</span>
            </span>
          </label>
        </div>
      </div>
      <button
        className="btn btn-success btn-sm mt-2 w-full py-2 text-base font-bold tracking-wide sm:btn-md sm:py-3 sm:text-lg"
        disabled={
          isSubmitting ||
          !formData.name ||
          !formData.surname ||
          !formData.email ||
          !formData.password ||
          !formData.phone ||
          !formData.company ||
          !formData.terms
        }
        type="submit"
      >
        {isSubmitting ? (
          <span className="loading loading-spinner loading-sm mr-2"></span>
        ) : null}
        {isSubmitting ? "Submitting..." : "Register as Courier"}
      </button>
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/60">
          <div className="w-full max-w-md rounded-2xl bg-base-100 p-6 shadow-2xl dark:bg-base-200 sm:p-8 lg:p-10">
            <h3
              className={`mb-3 text-xl font-bold sm:mb-4 sm:text-2xl ${
                modalType === "success"
                  ? "text-green-600 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {modalType === "success"
                ? "Registration Successful"
                : "Submission Failed"}
            </h3>
            <p className="whitespace-pre-line text-sm text-base-content dark:text-base-content/80 sm:text-base">
              {modalType === "success"
                ? "Your courier registration has been submitted successfully. Verify your email then log in and finish setting up your account and uploading your documents."
                : errorDetail ||
                  "There was an error submitting your registration. Please check your information and try again."}
            </p>
            <button
              className="btn btn-primary btn-sm mt-6 w-full rounded-full sm:btn-md sm:mt-8"
              onClick={() => {
                setShowModal(false);
                if (modalType === "success") window.location.href = "/";
              }}
            >
              Continue
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
